import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { editor, IPosition } from 'monaco-editor';
import IStandaloneCodeEditor = editor.IStandaloneCodeEditor;
import IStandaloneEditorConstructionOptions = editor.IStandaloneEditorConstructionOptions;
import IEditorOptions = editor.IEditorOptions;
import IModelContentChangedEvent = editor.IModelContentChangedEvent;
import ITextModel = editor.ITextModel;
import IUnicodeHighlightOptions = editor.IUnicodeHighlightOptions;
import { CommonModule } from '@angular/common';
import FindMatch = editor.FindMatch;
import { I18nService } from "@wms/app/i18n/services/i18n.service";
import { suggestions } from '@wavemaker/studio-runtime-integration';
import { IntellisenseGeneratorService } from './intellisense-generator.service';
import { EventManagerService } from '@wms/app/event-manager';
import { ScriptLoaderService } from '@wm/core';
import { monacoThemes } from './monacoThemes';
import { marked } from 'marked';
import { ProjectService } from "@wms/services";

declare var monaco: any;
declare const window: any;

window.MonacoEnvironment = {
    getWorkerUrl: function (moduleId: string, label: string) {
      let CDN_BASE_URL = process.env['CDN_BASE_URL'];
      if (CDN_BASE_URL && process.env['BASE_URL'] === process.env['CDN_BASE_URL']) {
        CDN_BASE_URL = '//' + window.location.host + process.env['BASE_URL'];
      }
        const baseURL = window.location.protocol + (CDN_BASE_URL ? CDN_BASE_URL : ('//' + window.location.host + (process.env['BASE_URL'] || '/studio/'))) + 'assets/monaco/min'
        return `data:text/javascript;charset=utf-8,${encodeURIComponent(`
            self.MonacoEnvironment = {
            baseUrl: '${baseURL}'
            };
            importScripts('${baseURL}/vs/base/worker/${moduleId}');`
        )}`;
    }
}


@Component({
    standalone: true,
    selector: 'wms-editor',
    templateUrl: './wms-editor.component.html',
    styleUrls: ['./wms-editor.component.less'],
    imports: [CommonModule]
})
export class WMSEditorComponent implements OnChanges, OnDestroy {
    @Input() options!: IStandaloneEditorConstructionOptions;
    @Input() customClasses?: any;
    @Input() isFileWorkSpace?: boolean = false;
    @Output() onChange!: EventEmitter<{options: IStandaloneEditorConstructionOptions}>;
    @Output() onSave!: EventEmitter<any>;
    @ViewChild("wmsEditor", { static: true }) wmsEditorEl!: ElementRef;
    baseURL: string = 'assets';
    wmsUnicodeHighlightOptions!: IUnicodeHighlightOptions;
    wmsEditor!: IStandaloneCodeEditor;
    wmsEditorModel!: ITextModel | null;
    wmsEditorPosition!: IPosition;
    wmsEditorContentFirstChange: boolean = false;
    defaultWMSEditorTheme: string = 'vs-dark';
    defaultWMSEditorFontSize: number = 12;
    appBaseMetaInfo: any;
    loadingEditor = false;
    editorLoadingTheme = this.defaultWMSEditorTheme;
    locale: any = inject(I18nService).getMessages();
    bundleLoaderPromise: Promise<{loaded: boolean}>
    private scriptLoaderService = inject(ScriptLoaderService);
    private disposeLib : any
    private pendingOperations: Promise<any>[] = [];
    private isDestroying = false;
    private isMarkup:boolean = false;
    private isFirstContentLoad = false;

    constructor(@Inject("APP_BUILD_META_INFO") appBaseMetaInfo: any, private intellisenseGenerator: IntellisenseGeneratorService, private eventManagerService: EventManagerService, private projectService: ProjectService) {
        this.appBaseMetaInfo = appBaseMetaInfo;
        this.baseURL = this.appBaseMetaInfo.CDN_BASE_URL ? this.appBaseMetaInfo.CDN_BASE_URL + this.baseURL : this.baseURL;
        this.onChange = new EventEmitter<{ options: IStandaloneEditorConstructionOptions }>();
        this.onSave = new EventEmitter<any>();
        this.eventManagerService.changeInActiveTabAsObservable().subscribe((data) => {
            if (this.disposeLib?.dispose) this.disposeLib.dispose()
            if(data === "SCRIPT" || data === "STYLE")  {
              this.intellisenseGenerator.setIsFileWorkSpace(false);
              this.addIntellisense();
            }  else {
              this.disposeLib?.dispose()
            }
            this.isMarkup =  data === 'MARKUP' ? true : false
            this.isFirstContentLoad = false
        })

        this.eventManagerService.treeNodeChangeEventObservable().subscribe((data) => {
            if(this.isMarkup && data){
                this.focusNodeInEditor(data)
            }
        })
    }
    private configureRequire(callback: Function): void {
        window.require([`${this.baseURL}/require.config.js`], (configFunction: any) => {
            configFunction(this.baseURL)
            callback()
        }, (err: any) => {
          console.error('Loading require.config.js failed', err);
        });
    }
    private loadWMSEditorScripts(callback: Function): void {
        window.require([`vs/editor/editor.main`], callback, (err:any) => {
          console.error('Loading vs/editor/editor.main failed', err);
        });
    }

    private addHTMLValidation() {
        if (this.options.language === 'html') {
            // Remove any previous markers
            monaco.editor.removeAllMarkers('html');

            // Create a validation function
            const validate = (model: editor.ITextModel) => {
                const markers: editor.IMarkerData[] = [];
                const content = model.getValue();

                // Validate incomplete attributes
                const incompleteAttributeRegex = /\b[a-z]+(?:=[^"\s>]|=(?=[\s>]))/g;
                const attributeMatches = Array.from(content.matchAll(incompleteAttributeRegex));

                for (const match of attributeMatches) {
                    const matchIndex = match.index || 0;
                    const matchedAttribute = match[0];

                    const lineNumber = model.getPositionAt(matchIndex).lineNumber;
                    const column = model.getPositionAt(matchIndex).column;

                    markers.push({
                        severity: monaco.MarkerSeverity.Error,
                        message: `Incomplete attribute: ${matchedAttribute}`,
                        startLineNumber: lineNumber,
                        startColumn: column,
                        endLineNumber: lineNumber,
                        endColumn: column + matchedAttribute.length
                    });
                }

                // Validate unclosed tags
                const validateUnclosedTags = () => {
                    // List of void/self-closing tags
                    const voidTags = new Set([
                        'area', 'base', 'br', 'col', 'embed', 'hr', 'img',
                        'input', 'link', 'meta', 'param', 'source', 'track', 'wbr'
                    ]);

                    // Stack to track open tags
                    const tagStack: {tag: string, index: number}[] = [];
                    const openTags: {tag: string, index: number}[] = [];

                    // Regular expression to match tags
                    const tagRegex = /<\/?([a-z0-9-]+)(?:\s[^>]*)?>/gi;
                    let match;

                    while ((match = tagRegex.exec(content)) !== null) {
                        const fullTag = match[0];
                        const tag = match[1].toLowerCase();

                        // Skip custom wm- tags
                        if (tag.startsWith('wm-')) continue;

                        // Check if it's a closing tag
                        if (fullTag.startsWith('</')) {
                            // If stack is empty or top of stack doesn't match, potential mismatch
                            if (tagStack.length === 0 || tagStack[tagStack.length - 1].tag !== tag) {
                                const lineNumber = model.getPositionAt(match.index || 0).lineNumber;
                                const column = model.getPositionAt(match.index || 0).column;

                                markers.push({
                                    severity: monaco.MarkerSeverity.Warning,
                                    message: `Mismatched closing tag: </${tag}>`,
                                    startLineNumber: lineNumber,
                                    startColumn: column,
                                    endLineNumber: lineNumber,
                                    endColumn: column + tag.length + 3
                                });
                            } else {
                                // Matching close tag found, remove from stack
                                tagStack.pop();
                            }
                        }
                        // Opening tag
                        else {
                            // Void tags don't need closing
                            if (!voidTags.has(tag)) {
                                // If it's not a self-closing tag
                                if (!fullTag.endsWith('/>')) {
                                    tagStack.push({tag, index: match.index || 0});
                                    openTags.push({tag, index: match.index || 0});
                                }
                            }
                        }
                    }

                    // Check for unclosed tags
                    for (const {tag, index} of tagStack) {
                        const lineNumber = model.getPositionAt(index).lineNumber;
                        const column = model.getPositionAt(index).column;

                        markers.push({
                            severity: monaco.MarkerSeverity.Warning,
                            message: `Unclosed tag: <${tag}>`,
                            startLineNumber: lineNumber,
                            startColumn: column,
                            endLineNumber: lineNumber,
                            endColumn: column + tag.length + 2
                        });
                    }
                };

                // Run validation
                validateUnclosedTags();

                // Publish markers
                monaco.editor.setModelMarkers(model, 'html', markers);
            };

            // Validate on model content change
            const disposable = this.wmsEditorModel?.onDidChangeContent(() => {
                if (this.wmsEditorModel) {
                    validate(this.wmsEditorModel);
                }
            });

            // Initial validation
            if (this.wmsEditorModel) {
                validate(this.wmsEditorModel);
            }

            // Store disposable to clean up later
            this.disposeLib = {
                dispose: () => {
                    disposable?.dispose();
                    monaco.editor.removeAllMarkers('html');
                }
            };
        }
    }

    private async initWMSEditor(domElement: HTMLElement): Promise<void> {
        this.setWMSEditorSettings();
        if(this.isFileWorkSpace) this.setTsxOptions();
        else {
          monaco.editor?.getModel(monaco.Uri.parse("file:///sample.tsx"))?.dispose();
        }
        this.addIntellisense();
      this.wmsEditor = monaco.editor.create(domElement, this.options) as editor.IStandaloneCodeEditor;
      this.wmsEditorModel = this.wmsEditor.getModel();
      if (this.wmsEditorModel) {
            this.configureThemes();
            this.setupBracketSelection(this.wmsEditor);
            if(this.options.language === 'sql' || this.options.language === 'mysql') {
                this.wmsEditor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
                    // Emit the save event with the current editor content
                    this.eventManagerService.emitExecuteQueryOnDB()
                });
            }
            this.addHTMLValidation()
            this.markdownPreview()
            this.wmsEditorModel.onDidChangeContent((e: editor.IModelContentChangedEvent): void => {
                if (this.wmsEditorModel && this.options.value !== this.wmsEditorModel.getValue()) {
                    this.wmsEditorContentFirstChange = true;
                    this.options.value = this.wmsEditorModel.getValue();
                    this.onChange.emit({ options: this.options });
                }
                this.isFirstContentLoad = true
            });
          this.wmsEditor.onDidChangeCursorPosition((e) => {
              if (this.options.language === 'html' && this.isMarkup) {
                  if(this.isFirstContentLoad){
                      const widgetName = this.getWidgetNameAtCursor();
                      this.eventManagerService.emitEditorCursorChangeEvent(widgetName)
                  }
              }
          });

          this.wmsEditor.onDidChangeModelContent((event) => {
              if (event.isFlush) {
                  const selectedWidgetId = $('.active-tree-element').data('nodename');
                  if (selectedWidgetId) {
                      setTimeout(() => {
                          this.focusNodeInEditor(selectedWidgetId);
                      })
                  }
              }
          });
        }
        return;
    }

    setrenderLineHighlight(flag: string) {
      this.wmsEditor.updateOptions({
        renderLineHighlight: flag
      } as IEditorOptions);
    }

    getWidgetNameAtCursor(): string | null {
        const model = this.wmsEditor.getModel();
        const position = this.wmsEditor.getPosition();

        if (!model || !position || (model.isDisposed && model.isDisposed())) return null;

        const fullText = model.getValue();
        const cursorOffset = model.getOffsetAt(position);

        const tagRegex = /<\/?(wm-[a-zA-Z0-9_-]+)([^>]*)>/g;     // Regex for tags
        const openTags: { tag: string; start: number; text: string; end?: number}[] = [];
        let match: RegExpExecArray | null;

        // extract only the exact `name` attribute
        function getExactName(tagText: string): string | null {
            const attrRegex = /([a-zA-Z0-9_-]+)\s*=\s*(['"])(.*?)\2/g;
            let m: RegExpExecArray | null;
            while ((m = attrRegex.exec(tagText)) !== null) {
                if (m[1] === "name") {
                    return m[3];
                }
            }
            return null;
        }
        while ((match = tagRegex.exec(fullText)) !== null) {
            const isClosing = match[0].startsWith("</");
            const tagName = match[1];
            const tagText = match[0];
            const openTagColumn = model.getPositionAt(match.index).column;
            const start = match[0].startsWith("<wm-") ? (match.index > openTagColumn ? match.index - openTagColumn : match.index) : match.index;
            const end = start + match[0].length;

            if (!isClosing) {
                openTags.push({ tag: tagName, text: tagText, start });  // Push opening tag

                // Cursor inside opening tag itself
                if (cursorOffset >= start && cursorOffset <= end) {
                    return getExactName(tagText);
                }
            } else {
                // Closing tag → find last open
                const lastIdx = openTags.map(t => t.tag).lastIndexOf(tagName);
                if (lastIdx !== -1) {
                    const lastOpen = openTags[lastIdx];
                    lastOpen.end = end;

                    // Cursor inside the full element (between opening & closing)
                    if (cursorOffset >= lastOpen.start && cursorOffset <= end) {
                        return getExactName(lastOpen.text);
                    }
                    openTags.splice(lastIdx, 1);
                }
            }
        }
        return null;
    }

    focusNodeInEditor(nodeName: any) {
        const widgetName = nodeName;

        const model = this.wmsEditor?.getModel();
      if (!model || (model.isDisposed && model.isDisposed())) return;
        const value = model.getValue();

        // Find the widget tag in code
        const regex = new RegExp(`<wm-[^\\s]+[^>]*name=["']${widgetName}["']`, "i");
        const match = value.match(regex);

        if (match && match.index !== undefined) {
            const position = model.getPositionAt(match.index);
            this.wmsEditor.revealPositionInCenter(position, 1);
            this.wmsEditor.setPosition(position);
            this.wmsEditor.focus();
        }
    }

    private async markdownPreview() {
        // Live markdown preview
        if (this.wmsEditorModel && this.options.language === 'markdown') {
            this.wmsEditorModel.onDidChangeContent(async() => {
                const content = this.wmsEditorModel?.getValue() || '';
                const previewElement = document.getElementById('markdown-preview');
                if (previewElement) {
                    previewElement.innerHTML = await marked(content); // Render markdown
                }
            });

            // Initial render
            const initialContent = this.wmsEditorModel.getValue() || '';
            const previewElement = document.getElementById('markdown-preview');
            if (previewElement) {
                previewElement.innerHTML = await marked(initialContent);
            }
        }
    }

    private configureThemes(): void {
        Object.entries(monacoThemes).forEach(([themeName, themeData]) => {
            const formattedThemeName = themeName
                .replace(/([A-Z])/g, '-$1')
                .toLowerCase()
                .replace(/^-/, '');

            if (!['vs', 'vs-dark'].includes(formattedThemeName)) {
                monaco.editor.defineTheme(formattedThemeName, themeData);
            }
        });
    }

    private setupBracketSelection(editor: IStandaloneCodeEditor) {
        editor.onMouseDown((e) => {
            if (e.event.detail !== 2) return; // Only handle double clicks

            const position = e.target.position;
            if (!position) return;

            const model = editor.getModel();
            if (!model) return;

            // Get the content at click position
            const lineContent = model.getLineContent(position.lineNumber);

            // Adjust column position since Monaco uses 2-based indexing
            const adjustedColumn = position.column - 2;
            const character = lineContent[adjustedColumn];
            if (character === '{') {
                const range = findMatchingBracket(
                    model,
                    position.lineNumber,
                    adjustedColumn + 1
                );

                if (range) {
                    editor.setSelection(new monaco.Selection(
                        range.startLineNumber,
                        range.startColumn,
                        range.endLineNumber,
                        range.endColumn
                    ));

                    e.event.preventDefault();
                    e.event.stopPropagation();
                }
            }

            // Handle closing brace click
            else if (character === '}') {
                let depth = 1; // Start at 1 since we're starting at a closing bracket

                // Search backward for opening bracket
                for (let i = position.lineNumber; i >= 1; i--) {
                    const line = model.getLineContent(i);
                    const startIndex = i === position.lineNumber ? adjustedColumn - 1 : line.length - 1;

                    for (let j = startIndex; j >= 0; j--) {
                        if (line[j] === '}') {
                            depth++;
                        } else if (line[j] === '{') {
                            depth--;
                            if (depth === 0) {
                                editor.setSelection(new monaco.Selection(
                                    i,
                                    j + 1,
                                    position.lineNumber,
                                    position.column
                                ));
                                e.event.preventDefault();
                                e.event.stopPropagation();
                                return;
                            }
                        }
                    }
                }
            }
        });

        function findMatchingBracket(model: { getLineCount: () => number; getLineContent: (arg0: any) => any; }, lineNumber: any, column: number) {
            let depth = 1; // Start at 1 since we're starting at an opening bracket
            let startLine = lineNumber;
            let startCol = column;

            // Search forward for closing bracket
            for (let i = lineNumber; i <= model.getLineCount(); i++) {
                const line = model.getLineContent(i);
                let j = (i === lineNumber) ? column : 0;

                for (; j < line.length; j++) {
                    if (line[j] === '{') {
                        depth++;
                    } else if (line[j] === '}') {
                        depth--;
                        if (depth === 0) {
                            return {
                                startLineNumber: startLine,
                                startColumn: startCol,
                                endLineNumber: i,
                                endColumn: j + 2 // Include the closing bracket
                            };
                        }
                    }
                }
            }
            return null;
        }
    }

    public async triggerFormat(content?: string, editorLang?: string, editorPosition?:any): Promise<any> {
        if ( this.isDestroying ||!this.projectService.isPrettificationEnabled()) {
          const isEditorAvailable = await this.ensureMonacoEditorLoaded();
          if (!isEditorAvailable || !this.wmsEditorModel || (this.wmsEditorModel.isDisposed && this.wmsEditorModel.isDisposed())) {
            return content || '';
          }
          // Critical: When prettification is disabled, we must still set the editor content.
          // Without this, tab switching leaves the previous tab's content visible (e.g. markup shows script).
          // Use setValue (not executeEdits) for tab switch - it clears the undo stack so Ctrl+Z won't undo to previous tab's content.
          if (content !== undefined && content !== null) {
            this.wmsEditorModel.setValue(content ?? '');
            const language = editorLang || this.options.language;
            if (language) {
              monaco.editor.setModelLanguage(this.wmsEditorModel, language);
            }
          }
          return this.wmsEditorModel.getValue();
        }else {
          const formatOperation = (async () => {
            const isEditorAvailable = await this.ensureMonacoEditorLoaded();
            if (!isEditorAvailable || !this.wmsEditorModel || (this.wmsEditorModel.isDisposed && this.wmsEditorModel.isDisposed())) {
                return content || '';
            }
            // Use setValue (not executeEdits) for tab switch - it clears the undo stack so Ctrl+Z won't undo to previous tab's content.
            if (content !== undefined && content !== null) {
              this.wmsEditorModel.setValue(content ?? '');
            }
            const language = editorLang || this.options.language;
            if (language) monaco.editor.setModelLanguage(this.wmsEditorModel, language);

            await new Promise(resolve => setTimeout(resolve, 200));
            try {
                await this.wmsEditor.getAction('editor.action.formatDocument').run();
            } catch (error) {
                console.warn('Formatting failed:', error);
            }

            if (editorPosition && editorPosition.currentPosition) {
              this.wmsEditor.setPosition(editorPosition.currentPosition);
              if (editorPosition.currentSelectionRange) {
                this.wmsEditor.setSelection(editorPosition.currentSelectionRange);
              }
              this.wmsEditor.setScrollPosition({
                scrollTop: editorPosition.currentScrollTop || 0,
                scrollLeft: editorPosition.currentScrollLeft || 0
              });
            }
            return this.wmsEditorModel.getValue();
        })();
        this.pendingOperations.push(formatOperation);
        try {
            return await formatOperation;
        } finally {
            this.pendingOperations = this.pendingOperations.filter(op => op !== formatOperation);
          }
        }
    }

    private async updateWMSEditor() {
        const isEditorAvailable = await this.ensureMonacoEditorLoaded()
        if (!isEditorAvailable) return
        this.addIntellisense()
        this.addHTMLValidation()
        this.setWMSEditorSettings()
        this.markdownPreview()
        if (this.options.value || this.options.value === '') {
            const content = this.options.value ?? '';
            // Use setValue for tab switch - it clears the undo stack so Ctrl+Z won't undo to previous tab's content.
            this.wmsEditor.setValue(content);
            this.wmsEditorContentFirstChange = false;
        }
        if (this.wmsEditorModel && this.options.language) {
            monaco.editor.setModelLanguage(this.wmsEditorModel, this.options.language);
        }
        if (this.options.fontSize) {
            this.wmsEditor.updateOptions({
                fontSize: this.options.fontSize
            } as IEditorOptions);
            localStorage.setItem('wms_editor_font_size', this.options.fontSize.toString());
        }
        if (this.options.theme) {
            monaco.editor.setTheme(this.options.theme);
            localStorage.setItem('wms_editor_theme', this.options.theme);
        }
    }

    public async initEditorBundle(): Promise<{loaded: boolean}> {
      return  new Promise((resolve, reject) => {
        if (window.monaco) {
          if(!this.wmsEditor) {
            this.initWMSEditor(this.wmsEditorEl.nativeElement);
          }
          resolve({loaded: true});
        }
        else {
          this.loadingEditor = true;
          this.editorLoadingTheme = this.options.theme || localStorage.getItem('wms_editor_theme') || this.defaultWMSEditorTheme;
          const wmsEditorLoaderScript: HTMLScriptElement = document.head.appendChild(this.scriptLoaderService.createLoaderScriptElement(this.baseURL));
          wmsEditorLoaderScript.onload = (): void => {
            this.configureRequire((): void => {
              this.loadWMSEditorScripts((): void => {
                if (window.monaco) {
                  this.initWMSEditor(this.wmsEditorEl.nativeElement);
                  this.loadingEditor = false;
                  resolve({loaded: true });
                }
              });
            });
          }
          wmsEditorLoaderScript.onerror = (error: any) => {
            this.loadingEditor = false;
            console.error('Monaco editor bundle loading failed ...', error);
            reject({loaded: false, error: error});
          };
        }
      });
    }

    public async ensureMonacoEditorLoaded(){
        if(!this.wmsEditor) {
            if(!this.bundleLoaderPromise){
               this.bundleLoaderPromise = this.initEditorBundle();
               const bundleStatus = await this.bundleLoaderPromise
               if(!bundleStatus.loaded) {
                return false;
              }
            }else{
                const bundleStatus = await this.bundleLoaderPromise
                if(!bundleStatus.loaded) {
                 return false;
               }
            }
            return true
        }
        return true
    }

    private setWMSEditorSettings(): void {
        this.options = this.options || {};
        this.wmsUnicodeHighlightOptions = this.wmsUnicodeHighlightOptions || {};
        this.wmsUnicodeHighlightOptions = {
            ...this.wmsUnicodeHighlightOptions,
            invisibleCharacters: false,
            ambiguousCharacters: false
        }
        this.options = {
            ...this.options,
            fontSize: this.options.fontSize || parseInt(localStorage.getItem('wms_editor_font_size') || this.defaultWMSEditorFontSize.toString()),
            theme: this.options.theme || localStorage.getItem('wms_editor_theme') || this.defaultWMSEditorTheme,
            value: this.options.value || '',
            automaticLayout: true,
            renderLineHighlight: 'none',
            wordWrap: 'on',
            wrappingIndent: 'indent',
            scrollBeyondLastLine: false,
            unicodeHighlight: this.wmsUnicodeHighlightOptions,
        }
    }
    ngOnChanges(changes: SimpleChanges): void {
        if (this.wmsEditor) {
            this.updateWMSEditor();
        }
        if(changes['options']?.isFirstChange() && changes['options']?.currentValue?.hasOwnProperty('value') && !this.wmsEditor){
          this.updateWMSEditor();
        }
    }

    async ngOnDestroy(): Promise<void> {
        this.isDestroying = true;
        this.isMarkup = false;
        // Wait for any pending format operations to complete
        if (this.pendingOperations.length > 0) {
            try {
                await Promise.all(this.pendingOperations);
            } catch (error) {
                console.error('Error completing pending operations during cleanup:', error);
            }
        }

        if (this.disposeLib?.dispose) {
            this.disposeLib.dispose();
        }

        if (this.wmsEditor) {
            this.wmsEditor.dispose();
        }
    }
    public didWMSEditorContentChange(): boolean {
        return this.wmsEditorContentFirstChange;
    }
    public async setEditorValue(editorValue: string) {
        const isEditorAvailable = await this.ensureMonacoEditorLoaded()
        if(!isEditorAvailable || !this.wmsEditorModel || (this.wmsEditorModel.isDisposed && this.wmsEditorModel.isDisposed())) return
        const content = editorValue ?? '';
        // Use setValue for programmatic content - it clears the undo stack so Ctrl+Z won't undo to previous tab's content.
        this.wmsEditor.setValue(content);
    }
    public setCursorPosition(cursorPosition: IPosition): void {
        this.wmsEditor.setPosition(cursorPosition);
        this.wmsEditor.revealPosition(cursorPosition);
    }
    public getCursorPosition() {
      return this.wmsEditor.getPosition();
    }
    public getLineCount() {
      return this.wmsEditor.getModel()?.getLineCount() || 0;
    }
    public setEditorFocus(): void {
        if (this.wmsEditorModel) {
            this.wmsEditorPosition = this.wmsEditorModel.getPositionAt(this.wmsEditorModel.getValueLength());
            this.wmsEditor.setSelection({
                startLineNumber: this.wmsEditorPosition.lineNumber,
                startColumn: this.wmsEditorPosition.column,
                endLineNumber: this.wmsEditorPosition.lineNumber,
                endColumn: this.wmsEditorPosition.column
            });
        }
        this.wmsEditor?.focus();
    }
    public resetEditorContentChange(): void {
        this.wmsEditorContentFirstChange = false;
    }
    // to find the position of a given text in the editor
    public findPosition(text: any): FindMatch | null | undefined {
        return this.wmsEditorModel?.findNextMatch(text, { column: 1, lineNumber: 1 }, false, false, null, true);
    }
    // to move the cursor to the insert position
    public setInsertPosition(position: any): void {
        let lineNumber: number = position?.range.startLineNumber + 1;
        let editorPos: IPosition = {
            lineNumber: lineNumber || 1,
            column: 1
        };
        this.setCursorPosition(editorPos);
    }
    // to insert text at the given cursor position
    public insertTextAtPosition(position: any, text: any): void {
        this.setInsertPosition(position);
        this.wmsEditor.trigger('keyboard', 'type', { text });
    }
    // to highlight a list of lines in the editor with the given className
    public highlightLines(highlightList: any, className: any): void {
        highlightList.length && highlightList.forEach((item: any) => {
            let matches: editor.FindMatch[] | undefined = this.wmsEditorModel?.findMatches(item, false, false, false, null, false);
            if (matches?.length) {
                matches.forEach((match: editor.FindMatch): void => {
                    this.wmsEditorModel?.deltaDecorations([], [
                        {
                            range: match.range,
                            options: {
                                isWholeLine: false,
                                inlineClassName: className
                            }
                        }
                    ]);
                });
            }
        });
    }

    public setReadonly(flag: boolean) {
      this.wmsEditor.updateOptions({
        readOnly: flag
      } as IEditorOptions);
    }
    private async addIntellisense() {
        try {
            if (this.disposeLib?.dispose) this.disposeLib.dispose()
            if (this.options.language === 'javascript') {
              this.disposeLib = await this.intellisenseGenerator.addJSSuggestions(monaco, suggestions)
            }
            if (this.options.language === 'css') {
              this.disposeLib = await this.intellisenseGenerator.addCssSuggestions(monaco)
            }
            if (this.options.language === 'sql') {
                this.disposeLib = await this.intellisenseGenerator.addSqlSuggestions(monaco)
            }
            if (this.options.language === 'mysql') {
                this.disposeLib = await this.intellisenseGenerator.addMySqlSuggestions(monaco)
            }
        } catch (error) {
            console.log(error)
        }
    }
    private setTsxOptions(): void {
      const uri = monaco.Uri.parse("file:///sample.tsx");
      // Ensure model exists or create new
      let model = monaco.editor.getModel(uri);
      if (!model) {
        model = monaco.editor.createModel("", "typescript", uri);
      }
      this.options.model = model;
      monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
        jsx: monaco.languages.typescript.JsxEmit.React,
        allowJs: true,
        target: monaco.languages.typescript.ScriptTarget.ESNext,
        moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      });
      monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
        noSemanticValidation: true,
        noSyntaxValidation: false
      });
    }
}
export interface IWMSStandaloneEditorConstructionOptions extends IStandaloneEditorConstructionOptions {
    model?: any;
    value?: string;
    language?: string;
    theme?: string;
    autoDetectHighContrast?: boolean;
    accessibilityHelpUrl?: string;
    ariaContainerElement?: HTMLElement;
    fontSize?: any;
    automaticLayout?: boolean;
    wordWrap?: 'off' | 'on' | 'wordWrapColumn' | 'bounded' | undefined;
    wrappingIndent?: 'none' | 'same' | 'indent' | 'deepIndent' | undefined;
    unicodeHighlight?: IUnicodeHighlightOptions;
    scrollBeyondLastLine?: boolean;
}
